
import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, merge, take } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { ReportAspectGroupViewModel, ReportService } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { Utilities } from '../../../services/utilities';


@Component({
    selector: 'app-report-aspect-group-details',
    templateUrl: './report-aspect-group-details.component.html',
    styleUrls: ['./report-aspect-group-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportAspectGroupDetailsComponent implements OnInit {
    @ViewChild('form', { static: true }) form: NgForm;

    reportAspectGroupForm: UntypedFormGroup;

    reportAspectGroupId: number;
    reportAspectGroupName: string;
    reportId: number;

    loadingIndicator: boolean = false;
    private isSaving: boolean = false;
    anySaveOperationsPerformed: boolean = false;
    hasPermissionToChange$: Observable<boolean>;

    constructor(
        public dialogRef: MatDialogRef<ReportAspectGroupDetailsComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private authService: AuthService,
        private reportService: ReportService,
        private formBuilder: UntypedFormBuilder,
        private snackBar: MatSnackBar,
        public dialog: MatDialog
    ) {
        this.reportAspectGroupId = data.reportAspectGroupId;
        this.reportAspectGroupName = data.reportAspectGroupName;
        this.reportId = data.reportId;
        this.hasPermissionToChange$ = data.hasPermissionToChange$;
    }

    ngOnInit(): void {
        this.buildForm();
        this.loadData(this.reportAspectGroupId);

        // Use this construct to handle the escape key for cancelling
        this.dialogRef.keydownEvents().pipe(map(r => new EventMapper(r, this))).subscribe((e) => {
                if (e.event.key === "Escape") {
                    e.component.cancel();
                }
            }
        );

    }

    public loadData(reportAspectGroupId: number): void {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;
        this.reportService.getAspectGroupByReportAspectGroupId(reportAspectGroupId).subscribe(ok => this.dataLoaded(ok),
            error => this.onDataLoadFailed(error));
    }

    dataLoaded(reportAspectGroup: ReportAspectGroupViewModel) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.resetForm(reportAspectGroup);
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"),
            this.translationService.getTranslation("FetchErrorMessage") +
            "\r\n" +
            Utilities.getHttpResponseMessage(error),
            MessageSeverity.error,
            error);
    }


    public getFormControl(name: string): AbstractControl {
        return this.reportAspectGroupForm.controls[name];
    }

    private buildForm() {
        this.reportAspectGroupForm = this.formBuilder.group({
            reportAspectGroupId: '',
            name: '',
            reportId: '',
            maxScore: '',
            score: '',
            scorePercentage: '',
            scoreLevelName: '',
            comments: '',
            previousComments: ''
        });
    }

    public resetForm(reportAspectGroup: ReportAspectGroupViewModel) {
        this.reportAspectGroupForm.reset({
            reportAspectGroupId: reportAspectGroup.reportAspectGroupId,
            name: reportAspectGroup.name,
            reportId: reportAspectGroup.reportId,
            maxScore: reportAspectGroup.maxScore,
            score: reportAspectGroup.score,
            scorePercentage: reportAspectGroup.scorePercentage,
            scoreLevelName: reportAspectGroup.scoreLevelName,
            comments: reportAspectGroup.comments,
            previousComments: reportAspectGroup.previousComments
        });
    }

    private prepareSave(): ReportAspectGroupViewModel {
        const formModel = this.reportAspectGroupForm.value;

        return {
            reportAspectGroupId: formModel.reportAspectGroupId,
            comments: formModel.comments,
            reportId: formModel.reportId
        };
    }

    public save(close: boolean) {
        if (!this.reportAspectGroupForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const reportAspectGroup = this.prepareSave();

        this.reportService.updateReportAspectgroup(reportAspectGroup).subscribe(
            t => this.saveCompleted(close, t),
            error => this.saveFailed(error));
    }

    public cancel() {
        this.alertService.resetStickyMessage();

        if (this.reportAspectGroupForm.dirty) {
            this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 })
                .onAction().subscribe(() => {
                        this.dialogRef.close(this.anySaveOperationsPerformed);
                }
                );
        } else {
            this.dialogRef.close(this.anySaveOperationsPerformed);
        }
    }

    private saveCompleted(close: boolean, reportAspectGroup: ReportAspectGroupViewModel) {

        this.isSaving = false;
        this.anySaveOperationsPerformed = true;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), reportAspectGroup.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        if (close) {
            this.dialogRef.close(true);
        } else {
            this.resetForm(reportAspectGroup);
        }
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.changeReports);
    }

}

export class EventMapper {
    component: ReportAspectGroupDetailsComponent;
    event: KeyboardEvent;

    constructor(event: KeyboardEvent, component: ReportAspectGroupDetailsComponent) {
        this.component = component;
        this.event = event;
    }
}

